import { CSSProperties, useEffect, useState } from 'react';
import CardSymphony from 'elements/CardSymphony';
import useCampaignBuilder from 'Hooks/useCampaignBuilder';
import { clsx } from 'modules/Utils';
import useScreen from 'Hooks/useScreen';
import useBrand from 'Hooks/useBrand';
import { DEFAULT_CURRENCY } from 'modules/Const';
import getSymbolFromCurrency from 'currency-symbol-map';
import millify from 'millify';
import { getCurrencyOffset, Currency } from 'pages/MarketingPage/utils';
import Loader from 'elements/Loader';
import { StarsIcon } from 'components/svg-icons';
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext';
import { useContext } from 'react';
import { styx } from 'modules/Utils';
import { CurrencyCodesType } from 'modules/Types';
import { getConvertedCurrency_api } from 'api/campaignApi';

export type CostPlaylistType = (currency?: CurrencyCodesType) => Promise<number>;

export type PlaylistTier = {
    tier: number;
    cost: CostPlaylistType;
    proDiscount: number;
    bgImage: string;
}

const convertCostToCurrency = async ({ cost, currency }: { cost: number, currency?: CurrencyCodesType }) => {
    if (!currency || currency === "USD") return cost
    const amountConverted = await getConvertedCurrency_api({ from: "USD", to: currency, amount: cost })
    if (amountConverted === 0) return cost
    return amountConverted
}


export const availableTiers: PlaylistTier[] = [
    {
        tier: 30000,
        proDiscount: 0.15,
        bgImage: 'assets/images/playlisting/30k.png',
        cost: async (currency?: CurrencyCodesType) => {
            return await convertCostToCurrency({ cost: 250, currency });
        },
    },
    {
        tier: 100000,
        proDiscount: 0.16666,
        bgImage: 'assets/images/playlisting/100k.png',
        cost: async (currency?: CurrencyCodesType) => {
            return await convertCostToCurrency({ cost: 600, currency });
        },
    },
    {
        tier: 300000,
        proDiscount: 0.15,
        bgImage: 'assets/images/playlisting/300k.png',
        cost: async (currency?: CurrencyCodesType) => {
            return await convertCostToCurrency({ cost: 1500, currency });
        },
    },
    {
        tier: 600000,
        proDiscount: 0.15,
        bgImage: 'assets/images/playlisting/600k.png',
        cost: async (currency?: CurrencyCodesType) => {
            return await convertCostToCurrency({ cost: 3000, currency });
        },
    },
    {
        tier: 1000000,
        proDiscount: 0.15,
        bgImage: 'assets/images/playlisting/1mil.png',
        cost: async (currency?: CurrencyCodesType) => {
            return await convertCostToCurrency({ cost: 6000, currency });
        },
    }
]



const tier_image_mapping: any = {
    30000: require("assets/images/playlisting/30k.png").default,
    100000: require("assets/images/playlisting/100k.png").default,
    300000: require("assets/images/playlisting/300k.png").default,
    600000: require("assets/images/playlisting/600k.png").default,
    1000000: require("assets/images/playlisting/1mil.png").default
};

type TiersToShowType = {
    tier: number;
    cost: number;
    proDiscount: number;
    bgImage: string;
    proPricingDifference: number;
    proPricing: number;
}

interface PlaylistingTierSelectorProps {
    className?: string;
    style?: CSSProperties;
}

const PlaylistingTierSelector = ({
    className,
    style = {},
}: PlaylistingTierSelectorProps) => {
    const { cssCalc, mobileView, mStyle } = useScreen();
    const { currentBrand, isProBrand } = useBrand();
    const { handleOpenModal } = useContext(UpgradeModalContext);
    const {
        selectedPlaylistingTier,
        setSelectedPlaylistingTier,
        selectedSongToPromote
    } = useCampaignBuilder();

    const [tiersToShow, setTiersToShow] = useState<TiersToShowType[]>([]);

    const currency = currentBrand?.currency?.code ?? DEFAULT_CURRENCY;
    const currencySymbol = getSymbolFromCurrency(currency);

    const getDiscountPricing = async (goal: {
        tier: number;
        cost: number;
        proDiscount: number;
    }) => {
        const { cost, proDiscount } = goal;
        const currencyOffset = getCurrencyOffset(currency as Currency);
        const proPricingDifference = currencyOffset === 100 ? cost * proDiscount : Math.trunc(cost * proDiscount);
        const proPricing = currencyOffset === 100 ? (cost * (1 - proDiscount)) : Math.trunc(cost * (1 - proDiscount));

        return {
            proPricingDifference,
            proPricing
        };
    };

    const initializeTiers = async () => {
        const tiers: TiersToShowType[] = [];

        for (const availableTier of availableTiers) {
            let cost = await availableTier.cost(currency);
            const { proPricingDifference, proPricing } = await getDiscountPricing({ ...availableTier, cost });
            tiers.push({
                ...availableTier,
                cost,
                proPricingDifference,
                proPricing,
            });
        }
        setTiersToShow(tiers);
    };

    useEffect(() => {
        initializeTiers();
    }, []);

    const handleSelectTier = async (goal: {
        tier: number;
        cost: number;
        proDiscount: number;
        bgImage: string;
        proPrice?: number;
    }) => {
        const { cost, proDiscount } = goal;
        const { proPricing } = await getDiscountPricing(goal);

        // Store the actual calculated cost as a number
        const finalCost = isProBrand ? proPricing : cost;
        const tierData = {
            tier: goal.tier,
            cost: finalCost,
            proDiscount: goal.proDiscount,
            bgImage: goal.bgImage
        };

        setSelectedPlaylistingTier(tierData);
    };

    const renderTierOptions = () => {
        if (!tiersToShow.length) {
            return <Loader />;
        }

        return (
            <div className="tiersContainer" style={sty.tiersContainer}>
                {tiersToShow.map((availableTier) => {
                    const {
                        tier,
                        cost,
                        bgImage,
                        proDiscount,
                        proPricingDifference,
                        proPricing
                    } = availableTier;

                    const goal = {
                        tier,
                        cost,
                        bgImage,
                        proDiscount,
                    };

                    const imageAsset = tier_image_mapping[tier];
                    const isSelected = selectedPlaylistingTier?.tier === tier;

                    return (
                        <div
                            key={tier}
                            onClick={() => handleSelectTier(goal)}
                            className="tierOption"
                            style={styx(
                                mStyle(sty, 'tierOption'),
                                isSelected && sty.tierOptionSelected
                            )}
                            onMouseEnter={(e) => {
                                if (!isSelected) {
                                    Object.assign(e.currentTarget.style, sty.tierOptionHover);
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSelected) {
                                    e.currentTarget.style.backgroundColor = '';
                                }
                            }}
                        >
                            <div className="tierContent" style={mStyle(sty, 'tierContent')}>
                                <div className="tierInfo" style={mStyle(sty, 'tierInfo')}>
                                    <div className="tierImageAndText" style={sty.tierImageAndText}>
                                        <div
                                            className="tierImage"
                                            style={styx(mStyle(sty, 'tierImage'), {
                                                backgroundImage: `url(${imageAsset})`
                                            })}
                                        >
                                            <p style={sty.tierImageText}>{millify(tier)}</p>
                                        </div>
                                        <div className="tierDetails" style={sty.tierDetails}>
                                            <p style={sty.tierTitle}>{tier.toLocaleString('en')} Followers</p>
                                            <p style={sty.tierSubtitle}>Pitch your song to playlists with a combined following of over {millify(tier)} followers.</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="pricingSection" style={mStyle(sty, 'pricingSection')}>
                                    <p style={sty.currentPrice}>
                                        {currencySymbol}{(isProBrand ? proPricing : cost).toLocaleString('en', {
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 2
                                        })}
                                    </p>
                                    {isProBrand && (
                                        <p style={mStyle(sty, 'originalPrice')}>
                                            {currencySymbol}{cost.toLocaleString('en', {
                                                maximumFractionDigits: 2,
                                                minimumFractionDigits: 2
                                            })}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <CardSymphony
            className={clsx('playlistingTierSelectorComponent', className)}
            style={style}
        >
            <div className="titleSection" style={mStyle(sty, 'titleSection')}>
                <h5 style={sty.title}>Select Playlisting Tier</h5>
                <p style={mStyle(sty, 'description')}>
                    Symphony will automatically pitch "{selectedSongToPromote?.name}" to our network of highly-vetted, user-generated Spotify playlists. Select the tier of playlisting support you're looking for.
                </p>
            </div>


            {renderTierOptions()}
        </CardSymphony>
    );
};


const sty: Record<string, CSSProperties> = {
    cardContent: {
        padding: 20
    },
    titleSection: {

    },
    titleSection_mobile: {

    },
    title: {
        color: '#333',
        fontWeight: 'bold',
        fontSize: 18,
        marginBottom: 8
    },
    description: {
        fontSize: 16,
        color: '#666',
        lineHeight: 1.5,
        margin: 0
    },
    description_mobile: {
        fontSize: 14
    },
    upgradePrompt: {
        background: '#FFF7EB',
        color: '#D18827',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        paddingLeft: 28,
        paddingRight: 28
    },
    upgradePrompt_mobile: {
        paddingLeft: 16,
        paddingRight: 16
    },
    upgradeText: {
        marginLeft: 4,
        fontSize: 14,
        margin: 0
    },
    upgradeButton: {
        fontSize: 12,
        color: '#8B5CF6',
        border: '1px solid #8B5CF6',
        fontWeight: 600,
        borderRadius: 20,
        marginLeft: 'auto',
        padding: '8px 16px',
        background: 'transparent',
        cursor: 'pointer'
    },
    tiersContainer: {
        padding: '16px 24px'
    },
    tierOption: {
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        borderRadius: 10,
        marginBottom: 20,
        border: '1px solid #c4c4c4'
    },
    tierOption_mobile: {
        marginBottom: 12
    },
    tierOptionSelected: {
        backgroundColor: '#F3F4F6',
        border: '2px solid #8B5CF6'
    },
    tierOptionHover: {
        backgroundColor: '#F9FAFB'
    },
    tierContent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%'
    },
    tierContent_mobile: {
        flexDirection: 'column'
    },
    tierInfo: {
        width: '75%'
    },
    tierInfo_mobile: {
        width: '100%'
    },
    tierImageAndText: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center'
    },
    tierImage: {
        fontSize: 24,
        fontWeight: 'bold',
        borderRadius: 8,
        width: 96,
        paddingTop: 40,
        paddingBottom: 40,
        textAlign: 'center' as const,
        marginRight: 12,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
    },
    tierImage_mobile: {
        width: '66%'
    },
    tierImageText: {
        margin: 0
    },
    tierDetails: {
        display: 'flex',
        flexDirection: 'column'
    },
    tierTitle: {
        fontSize: 18,
        fontWeight: 600,
        margin: 0,
        marginBottom: 4
    },
    tierSubtitle: {
        width: '100%',
        margin: 0,
        marginTop: 4,
        fontSize: 14
    },
    proSavings: {
        borderRadius: 20,
        display: 'flex',
        alignItems: 'center',
        padding: '4px 8px',
        fontSize: 12,
        background: '#FFF7EB',
        color: '#D18827',
        width: 'fit-content',
        marginTop: 8
    },
    proSavingsText: {
        marginLeft: 2,
        margin: 0
    },
    pricingSection: {
        marginTop: 0,
        marginLeft: 'auto'
    },
    pricingSection_mobile: {
        marginTop: 12,
        marginLeft: 0
    },
    currentPrice: {
        fontSize: 20,
        fontWeight: 600,
        color: '#8B5CF6',
        margin: 0
    },
    originalPrice: {
        textDecoration: 'line-through',
        fontSize: 14,
        textAlign: 'right' as const,
        fontWeight: 600,
        color: '#9CA3AF',
        margin: 0
    },
    originalPrice_mobile: {
        textAlign: 'center' as const
    }
};


export default PlaylistingTierSelector;
